import { Injectable, inject } from '@angular/core';
import { ref, getBlob } from 'firebase/storage';

import { FIREBASE_STORAGE } from '@core/firebase/firebase.tokens';
import { SaypStatementPdf } from '@save-as-you-pay/models/sayp-statement-pdf.model';

/**
 * Service for downloading statement PDFs from Firebase Storage
 */
@Injectable({
  providedIn: 'root',
})
export class StatementPdfDownloadService {
  private readonly storage = inject(FIREBASE_STORAGE);

  async downloadPdfFromPath(
    storagePath: string,
    filename: string
  ): Promise<void> {
    try {
      const fileRef = ref(this.storage, storagePath);
      const blob = await getBlob(fileRef);

      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();

      await new Promise(resolve => globalThis.setTimeout(resolve, 100));
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading PDF from storage:', error);
      throw error;
    }
  }

  async downloadPdfsSeparately(
    pdfs: Array<SaypStatementPdf & { storagePath: string }>
  ): Promise<void> {
    for (let i = 0; i < pdfs.length; i++) {
      const pdf = pdfs[i];
      await this.downloadPdfFromPath(
        pdf.storagePath,
        `statement-${pdf.accountId}.pdf`
      );

      if (i < pdfs.length - 1) {
        await new Promise(resolve => globalThis.setTimeout(resolve, 300));
      }
    }
  }

  async downloadPdfsCombined(
    pdfs: Array<SaypStatementPdf & { storagePath: string }>,
    filename: string
  ): Promise<void> {
    try {
      const { PDFDocument } = await import('pdf-lib');

      const mergedPdf = await PDFDocument.create();

      for (const pdf of pdfs) {
        try {
          const fileRef = ref(this.storage, pdf.storagePath);
          const blob = await getBlob(fileRef);

          const pdfBytes = await blob.arrayBuffer();
          const pdfDoc = await PDFDocument.load(pdfBytes);
          const pages = await mergedPdf.copyPages(
            pdfDoc,
            pdfDoc.getPageIndices()
          );

          pages.forEach(page => mergedPdf.addPage(page));
        } catch (error) {
          console.error(`Error loading PDF ${pdf.id}:`, error);
          throw new Error(
            `Failed to load PDF for account ${pdf.accountId}: ${error instanceof Error ? error.message : 'Unknown error'}`,
            { cause: error }
          );
        }
      }

      const mergedPdfBytes = await mergedPdf.save();
      const blob = new Blob([mergedPdfBytes as BlobPart], {
        type: 'application/pdf',
      });
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();

      await new Promise(resolve => globalThis.setTimeout(resolve, 100));
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error combining PDFs:', error);
      throw error;
    }
  }
}
